import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MeasurementSessionStatus, TERMINAL_SESSION_STATUSES } from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { LoadingState } from '@/components/ui/LoadingState';
import { Modal } from '@/components/ui/Modal';
// The measurement and its MediaPipe engine are megabytes; they load when a technician opens this, not on every page.
const AssistedPdCapture = lazy(() => import('@/modules/capture/AssistedPdCapture'));
import { candidateFrom, outcomeFrom } from '@/modules/capture/submitEstimate';
import { coarseDeviceClass } from '@/modules/capture/captureClient';
import type { AssistedAttempt } from '@/modules/capture/vision/useAssistedCapture';
import { apiErrorMessage } from '@/services/apiClient';
import {
  useCancelMeasurementSessionMutation,
  useCreateMeasurementSessionMutation,
  useMeasurementSessionQuery,
  useRecordAttemptMutation,
  useStartDeviceCaptureMutation,
  useSubmitDeviceCandidateMutation,
} from '../api/measurements.api';
import { CameraPdReview } from './CameraPdReview';

/**
 * A camera PD estimate taken on the desk's own device.
 *
 * The same capture the phone runs, on this screen instead: the session is created for LIVE_CAMERA,
 * started here, and its result offered for review in the same panel, where the technician's own
 * figure is the one that is saved. Closing before the session ends cancels it.
 */
export function DeviceCaptureDialog({
  customerId,
  isOpen,
  onClose,
}: {
  customerId: string;
  isOpen: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  const create = useCreateMeasurementSessionMutation();
  const start = useStartDeviceCaptureMutation();
  const submit = useSubmitDeviceCandidateMutation();
  const attempt = useRecordAttemptMutation();
  const cancel = useCancelMeasurementSessionMutation();

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [delivery, setDelivery] = useState<{ state: 'idle' | 'sending' | 'sent' | 'failed' }>({
    state: 'idle',
  });
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  const session = useMeasurementSessionQuery(sessionId, false);
  const status = session.data?.status ?? null;

  useEffect(() => {
    // Effects run twice in development; one dialog opening is one session.
    if (!isOpen || started.current) return;
    started.current = true;

    create.mutate(
      { customerId, requestedSource: 'LIVE_CAMERA' },
      {
        onSuccess: (created) => {
          setSessionId(created.id);
          start.mutate(created.id, {
            onError: (caught) =>
              setError(apiErrorMessage(caught) ?? t('measurements.phone.errors.start')),
          });
        },
        onError: (caught) =>
          setError(apiErrorMessage(caught) ?? t('measurements.phone.errors.start')),
      },
    );
    // The mutations are stable for the dialog's life; only opening it should start a session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, customerId]);

  const close = () => {
    if (sessionId && status && !TERMINAL_SESSION_STATUSES.includes(status))
      cancel.mutate(sessionId);
    started.current = false;
    setSessionId(null);
    setDelivery({ state: 'idle' });
    setError(null);
    onClose();
  };

  /** Send the estimate, or — when the capture produced no number — record the attempt and say nothing else. */
  const deliver = (finished: AssistedAttempt) => {
    if (!sessionId) return;
    setError(null);
    const candidate = candidateFrom(finished, coarseDeviceClass());
    if (!candidate) {
      attempt.mutate({ sessionId, outcome: outcomeFrom(finished) });
      return;
    }
    setDelivery({ state: 'sending' });
    submit.mutate(
      { sessionId, candidate },
      {
        onSuccess: () => {
          setDelivery({ state: 'sent' });
          void session.refetch();
        },
        onError: (caught) => {
          setDelivery({ state: 'failed' });
          setError(apiErrorMessage(caught) ?? t('capture.pd.sendFailed'));
        },
      },
    );
  };

  const underReview =
    status === MeasurementSessionStatus.AWAITING_REVIEW && session.data?.candidate
      ? session.data
      : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={t('measurements.device.title')}
      description={t('measurements.device.description')}
      footer={
        <Button variant="secondary" onClick={close}>
          {status && !TERMINAL_SESSION_STATUSES.includes(status)
            ? t('measurements.phone.cancel')
            : t('common.close')}
        </Button>
      }
    >
      <div className="space-y-4">
        {error ? <Alert tone="error">{error}</Alert> : null}
        {session.data?.isValidation ? (
          <Alert tone="warning">{t('measurements.phone.validation')}</Alert>
        ) : null}

        {underReview ? (
          <CameraPdReview session={underReview} customerId={customerId} onDone={close} />
        ) : (
          <div className="flex justify-center">
            <Suspense fallback={<LoadingState />}>
              <AssistedPdCapture onResult={deliver} delivery={delivery} />
            </Suspense>
          </div>
        )}
      </div>
    </Modal>
  );
}
